import React, {useMemo} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Menu} from '@mantine/core';
import {Space} from 'src/design/atoms/Space';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {AuditLogSimpleResponse} from 'src/api/ProactApiClient';
import {Description} from 'src/design/atoms/Description';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetAuditLogListV2Query} from 'src/redux/queries/auditLog';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const AuditLogsTable: React.FC = () => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<AuditLogSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Audit Log ID',
        enableSorting: false,
      },
      {
        accessorKey: 'entityName',
        header: 'Object Type',
        enableSorting: false,
      },
      {
        accessorKey: 'entityId',
        header: 'Object ID',
        enableSorting: false,
      },
      {
        accessorKey: 'actionType',
        header: 'Action Type',
        enableSorting: false,
      },
      {
        accessorKey: 'creator.name',
        header: 'Created By',
        enableSorting: false,
      },
      {
        id: 'createdAt',
        accessorFn: row => (row.createdAt ? new Date(row.createdAt) : null),
        filterVariant: 'date',
        header: 'Created On',
        enableSorting: false,
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'audit-log-v2',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'id', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isFetching, isError, refetch} =
    useGetAuditLogListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
    });

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleNavigateToEntity = (row: AuditLogSimpleResponse) =>
    navigate(PathUtility.AuditLogView(row.id));

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => handleNavigateToEntity(row.original)}>
            View
          </Menu.Item>
        </>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      <Description text="Open an audit log record to compare changes to a previous version. Audit data is automatically deleted based on your environment's data storage agreement. Some data is automatically created, updated, or deleted by the system but may be attributed to a user as part of a user-initiated action." />
      <Space pb="lg" />
      {table}
    </>
  );
};
